import { useEffect } from "react";

import { useCan } from "components";
import { createFormHelper } from "lib/Form";
import { NOTE_SUGGESTION_TYPES, PIN_TYPES, STATUS_IDENTIFIERS } from "models";
import { useNoteSuggestions } from "modules/AppointmentDetails/hooks";

import type { DropdownItemProps } from "components";
import type { Appointment } from "models";
import type { NoteAttachment } from "modules/AppointmentDetails/components/Interventions/components/NoteAttachments";
import type { PinCategoryFormValues } from "modules/AppointmentDetails/components/Interventions/components/PinCategory/components/PinCategoryForm";
import type { Dispatch, SetStateAction } from "react";

type PinCategoryFormProps = {
  selectedPinTypeOption: DropdownItemProps<PIN_TYPES>;
  hasChanges: boolean;
  appointment?: Appointment;
  attachments: NoteAttachment[];
  setHasChanges: Dispatch<SetStateAction<boolean>>;
  setAttachments: Dispatch<SetStateAction<NoteAttachment[]>>;
};

export const PinToNoteTypeMapping = new Map<PIN_TYPES | null, NOTE_SUGGESTION_TYPES>([
  [PIN_TYPES.Warranty, NOTE_SUGGESTION_TYPES.WarrantyPin],
  [PIN_TYPES.Recall, NOTE_SUGGESTION_TYPES.RecallPin],
  [PIN_TYPES.Remarks, NOTE_SUGGESTION_TYPES.RemarkPin]
]);

const FormHelper = createFormHelper<PinCategoryFormValues>();

export const usePinCategoryForm = ({ appointment, selectedPinTypeOption, attachments, hasChanges, setHasChanges, setAttachments }: PinCategoryFormProps) => {
  const {
    setValue,
    watch,
    formState: { isDirty, isSubmitSuccessful },
    reset
  } = FormHelper.useFormContext();

  const [note, visibleImportantItems, orderStatus, keepParts] = watch(["note", "visible_important_items", "order_status", "keep_parts"]);

  const canUpdateInterventions = useCan("update", "appointments");
  const noteSuggestionOptions = useNoteSuggestions(PinToNoteTypeMapping.get(selectedPinTypeOption.value), appointment?.dealer_location_id);

  const isNoteRequired = visibleImportantItems && !note && !keepParts;
  const isButtonDisabled = isNoteRequired || !hasChanges || !canUpdateInterventions || appointment?.appointment_status_identifier === STATUS_IDENTIFIERS.CanceledStatus;

  const handleNoteSuggestionDropdownChange = (value?: number | null) => {
    const noteSuggestion = noteSuggestionOptions.find(item => item.value === value)?.text;
    if (!noteSuggestion) return;

    setValue("note", `${note}${note && "\n"}${noteSuggestion}`);
  };

  const handleKeepPartsCheckboxChange = (value: boolean) => {
    if (value) setValue("visible_important_items", true);
    else if (!note) setValue("visible_important_items", false);
  };

  useEffect(() => {
    setHasChanges(isDirty || attachments.length > 0);
    if (isSubmitSuccessful) {
      reset();
      setAttachments([]);
    }
  }, [isDirty, isSubmitSuccessful, attachments]);

  return { orderStatus, isButtonDisabled, noteSuggestionOptions, isNoteRequired, handleKeepPartsCheckboxChange, handleNoteSuggestionDropdownChange };
};
